import React from 'react';

import { FaChevronDown, FaSearch, FaTrash } from 'react-icons/fa';

import LoadingLogo from '~/icons/fabletics/LoadingIndicator';

import {
  AddCircle,
  AddToCart,
  Alert,
  AlertCircle,
  AlertTriangle,
  Calendar,
  Camera,
  CaretLeft,
  CaretRight,
  CaretUp,
  CheckCircle,
  Clock,
  Close,
  CloseCircle,
  DeliveryStatus,
  Dislike,
  Edit,
  EmptyStar,
  Favorites,
  FilledStar,
  Filter,
  Gift,
  HappyReturnsClock,
  HappyReturnsLeaf,
  HappyReturnsLocation,
  HappyReturnsOffer,
  Heart,
  Home,
  Information,
  Leaf,
  Like,
  LocationPin,
  Mail,
  NavigationLeft,
  Plus,
  PrinterReturnItems,
  QuestionCircle,
  Receipt,
  ScanBarcode,
  ScrubsThread,
  Shipping1,
  Shipping2,
  Stars,
  Tag1,
  Tag2,
  ToggleDown,
  ViewOff,
  ViewOn,
} from '../../fabletics';
import { type SavageIconTypes } from '../../savage/SavageIcons';
import BowIcon from '../BowIcon';
import CartIcon from '../CartIcon';
import ChatBubbleIcon from '../ChatBubbleIcon';
import ChatsIcon from '../ChatsIcon';
import EmailIcon from '../EmailIcon';

export type IconName =
  | 'bow'
  | 'addCircle'
  | 'addToCart'
  | 'alert'
  | 'alertCircle'
  | 'alertTriangle'
  | 'calendar'
  | 'camera'
  | 'checkoutLoadingIndicator'
  | 'chevronLeft'
  | 'chevronRight'
  | 'chevronDown'
  | 'chevronUp'
  | 'cart'
  | 'chat'
  | 'chatAlt'
  | 'checkCircle'
  | 'clock'
  | 'close'
  | 'closeCircle'
  | 'deliveryStatus'
  | 'dislike'
  | 'edit'
  | 'email'
  | 'emptyStar'
  | 'favorites'
  | 'filledStar'
  | 'filter'
  | 'gift'
  | 'happyReturnsClock'
  | 'happyReturnsLeaf'
  | 'happyReturnsLocation'
  | 'happyReturnsOffer'
  | 'heart'
  | 'home'
  | 'information'
  | 'leaf'
  | 'like'
  | 'locationPin'
  | 'mail'
  | 'navigationLeft'
  | 'plus'
  | 'printerReturnItems'
  | 'questionCircle'
  | 'receipt'
  | 'scanBarcode'
  | 'scrubsThread'
  | 'search'
  | 'shipping1'
  | 'shipping2'
  | 'stars'
  | 'tag1'
  | 'tag2'
  | 'toggleDown'
  | 'trash'
  | 'viewOff'
  | 'viewOn';

export function isSavageIconProps(icon: any): icon is SavageIconProps {
  return icon && typeof icon === 'object' && 'type' in icon;
}
type SavageIconProps = {
  type: SavageIconTypes;
  // Additional props.
  [key: string]: any;
};

type IconSet = {
  [key in IconName]: React.ElementType;
};

type SavageIconSet = {
  [key in IconName]: React.ElementType | SavageIconProps;
};

// FableticsIcons
export const fableticsIcons: IconSet = {
  bow: BowIcon,
  addCircle: AddCircle,
  addToCart: AddToCart,
  alert: Alert,
  alertCircle: AlertCircle,
  alertTriangle: AlertTriangle,
  calendar: Calendar,
  camera: Camera,
  checkoutLoadingIndicator: LoadingLogo,
  chevronLeft: CaretLeft,
  chevronRight: CaretRight,
  chevronDown: FaChevronDown,
  chevronUp: CaretUp,
  cart: CartIcon,
  chat: ChatBubbleIcon,
  chatAlt: ChatsIcon,
  checkCircle: CheckCircle,
  clock: Clock,
  close: Close,
  closeCircle: CloseCircle,
  deliveryStatus: DeliveryStatus,
  dislike: Dislike,
  edit: Edit,
  email: EmailIcon,
  emptyStar: EmptyStar,
  favorites: Favorites,
  filledStar: FilledStar,
  filter: Filter,
  gift: Gift,
  happyReturnsClock: HappyReturnsClock,
  happyReturnsLeaf: HappyReturnsLeaf,
  happyReturnsLocation: HappyReturnsLocation,
  happyReturnsOffer: HappyReturnsOffer,
  heart: Heart,
  home: Home,
  information: Information,
  leaf: Leaf,
  like: Like,
  locationPin: LocationPin,
  mail: Mail,
  navigationLeft: NavigationLeft,
  plus: Plus,
  printerReturnItems: PrinterReturnItems,
  questionCircle: QuestionCircle,
  receipt: Receipt,
  scanBarcode: ScanBarcode,
  scrubsThread: ScrubsThread,
  search: FaSearch,
  shipping1: Shipping1,
  shipping2: Shipping2,
  stars: Stars,
  tag1: Tag1,
  tag2: Tag2,
  toggleDown: ToggleDown,
  trash: FaTrash,
  viewOff: ViewOff,
  viewOn: ViewOn,
};

// SavageIcons
export const savageIcons: SavageIconSet = {
  // Uses `SavageIcons`
  chevronLeft: {
    type: 'caretLeft',
  },
  chevronRight: {
    type: 'caretRight',
  },
  chevronUp: {
    type: 'caretUp',
  },
  chevronDown: {
    type: 'caretDown',
  },
  cart: {
    type: 'cart',
  },
  close: {
    type: 'close',
  },
  heart: {
    type: 'heart',
  },
  email: {
    type: 'email',
  },
  emptyStar: {
    type: 'starOutline',
    fill: 'none',
  },
  filledStar: {
    type: 'star',
  },
  search: {
    type: 'search',
  },
  trash: {
    type: 'trashcan',
  },
  chat: {
    type: 'chat',
  },
  chatAlt: {
    type: 'chatAlt',
  },
  checkCircle: {
    type: 'checkCircle',
  },
  checkoutLoadingIndicator: {
    type: 'checkoutLoadingIndicator',
  },
  // Fabletics Defaults
  bow: BowIcon,
  addCircle: AddCircle,
  addToCart: AddToCart,
  alert: Alert,
  alertCircle: AlertCircle,
  alertTriangle: AlertTriangle,
  calendar: Calendar,
  camera: Camera,
  clock: Clock,
  closeCircle: CloseCircle,
  deliveryStatus: DeliveryStatus,
  dislike: Dislike,
  edit: Edit,
  favorites: Favorites,
  filter: Filter,
  gift: Gift,
  happyReturnsClock: HappyReturnsClock,
  happyReturnsLeaf: HappyReturnsLeaf,
  happyReturnsLocation: HappyReturnsLocation,
  happyReturnsOffer: HappyReturnsOffer,
  home: Home,
  information: Information,
  leaf: Leaf,
  like: Like,
  locationPin: LocationPin,
  mail: Mail,
  navigationLeft: NavigationLeft,
  plus: Plus,
  printerReturnItems: PrinterReturnItems,
  questionCircle: QuestionCircle,
  receipt: Receipt,
  scanBarcode: ScanBarcode,
  scrubsThread: ScrubsThread,
  shipping1: Shipping1,
  shipping2: Shipping2,
  stars: Stars,
  tag1: Tag1,
  tag2: Tag2,
  toggleDown: ToggleDown,
  viewOff: ViewOff,
  viewOn: ViewOn,
};
